import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { ArrowLeft, MessageSquare, BarChart3, FileText, Settings, Sparkles, PresentationIcon } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ConversationsTab from '../components/ConversationsTab';
import DashboardTab from '../components/DashboardTab';
import SummaryTab from '../components/SummaryTab';
import SettingsTab from '../components/SettingsTab';
import ResearchChatTab from '../components/ResearchChatTab';
import ICSummaryTab from '../components/ICSummaryTab';

export default function ProjectDetailPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, API } = useAuth();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('chat');

  useEffect(() => {
    fetchProject();
  }, [projectId]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${API}/projects/${projectId}`, getAuthHeaders());
      setProject(response.data);
    } catch (error) {
      console.error('Failed to fetch project:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Project not found</h2>
          <Button onClick={() => navigate('/projects')} className="btn-primary">
            Back to Projects
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="glassmorphism sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4 mb-3">
            <Button
              onClick={() => navigate('/projects')}
              variant="ghost"
              size="sm"
              className="text-slate-600 hover:text-slate-900"
              data-testid="back-to-projects-button"
            >
              <ArrowLeft size={18} />
            </Button>
            <div>
              <h1 className="text-2xl font-heading font-bold tracking-tight text-slate-900">{project.name}</h1>
              {project.description && (
                <p className="text-sm text-slate-600 mt-1">{project.description}</p>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <main className="max-w-7xl mx-auto px-6 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6 max-w-4xl mb-8">
            <TabsTrigger value="chat" className="flex items-center gap-2" data-testid="research-chat-tab">
              <Sparkles size={16} />
              <span className="hidden sm:inline">Chat</span>
            </TabsTrigger>
            <TabsTrigger value="ic-summary" className="flex items-center gap-2" data-testid="ic-summary-tab">
              <PresentationIcon size={16} />
              <span className="hidden sm:inline">IC Summary</span>
            </TabsTrigger>
            <TabsTrigger value="conversations" className="flex items-center gap-2" data-testid="conversations-tab">
              <MessageSquare size={16} />
              <span className="hidden sm:inline">Conversations</span>
            </TabsTrigger>
            <TabsTrigger value="dashboard" className="flex items-center gap-2" data-testid="dashboard-tab">
              <BarChart3 size={16} />
              <span className="hidden sm:inline">Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="summary" className="flex items-center gap-2" data-testid="summary-tab">
              <FileText size={16} />
              <span className="hidden sm:inline">Summary</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2" data-testid="settings-tab">
              <Settings size={16} />
              <span className="hidden sm:inline">Settings</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="chat">
            <ResearchChatTab projectId={projectId} />
          </TabsContent>

          <TabsContent value="ic-summary">
            <ICSummaryTab projectId={projectId} />
          </TabsContent>

          <TabsContent value="conversations">
            <ConversationsTab projectId={projectId} />
          </TabsContent>

          <TabsContent value="dashboard">
            <DashboardTab projectId={projectId} />
          </TabsContent>

          <TabsContent value="summary">
            <SummaryTab projectId={projectId} />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsTab project={project} refreshProject={fetchProject} />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
